import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  LayoutDashboard, Calendar, Package, Truck, Shirt, 
  LogOut, Plus, Edit, Trash2, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, Menu, X, Loader2, Users, Eye, Mail, Phone, MapPin, FileText, Building2, Inbox, ClipboardCheck, KeyRound
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { logError, logWarn } from "@/lib/log";
import { QuoteRequestsPanel } from "@/pages/admin/QuoteRequestsPanel";
import { EnquiriesPanel } from "@/pages/admin/EnquiriesPanel";
import { JobSheetsPanel } from "@/pages/admin/JobSheetsPanel";
import { CleanersPanel } from "@/pages/admin/CleanersPanel";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_laundry-dispatch/artifacts/ce064zn3_Copy%20of%20BusCard%20SB%201.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [laundryServices, setLaundryServices] = useState([]);
  const [recruitmentApplications, setRecruitmentApplications] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationDetailModal, setApplicationDetailModal] = useState(false);

  // Modal states
  const [supplierModal, setSupplierModal] = useState(false);
  const [stockModal, setStockModal] = useState(false);
  const [laundryModal, setLaundryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const adminName = localStorage.getItem("admin_name");
  const isLoggedIn = localStorage.getItem("admin_logged_in") === "true";

  // axios.defaults.withCredentials is true (set in App.js) — auth cookie sent automatically
  const axiosConfig = {};

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, suppliersRes, stockRes, laundryRes, recruitmentRes, quoteRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/suppliers`),
        axios.get(`${API}/admin/stock`),
        axios.get(`${API}/admin/laundry`),
        axios.get(`${API}/admin/recruitment`),
        axios.get(`${API}/admin/quote-requests`),
        axios.get(`${API}/admin/enquiries`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setSuppliers(suppliersRes.data);
      setStockItems(stockRes.data);
      setLaundryServices(laundryRes.data);
      setRecruitmentApplications(recruitmentRes.data);
      setQuoteRequests(quoteRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (error) {
      logError("Error fetching admin dashboard data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_logged_in");
        localStorage.removeItem("admin_name");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [isLoggedIn, navigate, fetchAllData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (err) {
      // Non-fatal: clear local state so the user is signed out client-side regardless.
      logWarn("Admin logout request failed; clearing local state anyway.", err);
    }
    localStorage.removeItem("admin_logged_in");
    localStorage.removeItem("admin_name");
    navigate("/admin");
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {}, axiosConfig);
      toast.success("Booking status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Supplier CRUD
  const [supplierForm, setSupplierForm] = useState({
    name: "", contact_email: "", contact_phone: "", address: "", products: "", notes: ""
  });

  const handleSupplierSubmit = async () => {
    try {
      const data = {
        ...supplierForm,
        products: supplierForm.products.split(",").map(p => p.trim()).filter(p => p)
      };
      if (editingItem) {
        await axios.patch(`${API}/admin/suppliers/${editingItem.id}`, data, axiosConfig);
        toast.success("Supplier updated");
      } else {
        await axios.post(`${API}/admin/suppliers`, data, axiosConfig);
        toast.success("Supplier added");
      }
      setSupplierModal(false);
      setEditingItem(null);
      setSupplierForm({ name: "", contact_email: "", contact_phone: "", address: "", products: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save supplier");
    }
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${id}`, axiosConfig);
      toast.success("Supplier deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete supplier");
    }
  };

  // Stock CRUD
  const [stockForm, setStockForm] = useState({
    name: "", category: "", quantity: 0, unit: "", reorder_level: 10, supplier_id: "", eco_friendly: true
  });

  const handleStockSubmit = async () => {
    try {
      if (editingItem) {
        await axios.patch(`${API}/admin/stock/${editingItem.id}`, stockForm, axiosConfig);
        toast.success("Stock item updated");
      } else {
        await axios.post(`${API}/admin/stock`, stockForm, axiosConfig);
        toast.success("Stock item added");
      }
      setStockModal(false);
      setEditingItem(null);
      setStockForm({ name: "", category: "", quantity: 0, unit: "", reorder_level: 10, supplier_id: "", eco_friendly: true });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save stock item");
    }
  };

  const deleteStockItem = async (id) => {
    if (!window.confirm("Delete this stock item?")) return;
    try {
      await axios.delete(`${API}/admin/stock/${id}`, axiosConfig);
      toast.success("Stock item deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete stock item");
    }
  };

  // Laundry CRUD
  const [laundryForm, setLaundryForm] = useState({
    name: "", description: "", price_per_kg: 0, turnaround_days: 1, items_accepted: ""
  });

  const handleLaundrySubmit = async () => {
    try {
      const data = {
        ...laundryForm,
        items_accepted: laundryForm.items_accepted.split(",").map(i => i.trim()).filter(i => i)
      };
      if (editingItem) {
        await axios.patch(`${API}/admin/laundry/${editingItem.id}`, data, axiosConfig);
        toast.success("Laundry service updated");
      } else {
        await axios.post(`${API}/admin/laundry`, data, axiosConfig);
        toast.success("Laundry service added");
      }
      setLaundryModal(false);
      setEditingItem(null);
      setLaundryForm({ name: "", description: "", price_per_kg: 0, turnaround_days: 1, items_accepted: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save laundry service");
    }
  };

  const deleteLaundryService = async (id) => {
    if (!window.confirm("Delete this laundry service?")) return;
    try {
      await axios.delete(`${API}/admin/laundry/${id}`, axiosConfig);
      toast.success("Laundry service deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete laundry service");
    }
  };

  // Recruitment Application Status Update
  const updateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.patch(`${API}/admin/recruitment/${applicationId}/status?status=${status}`, {}, axiosConfig);
      toast.success("Application status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getApplicationStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      reviewed: "bg-blue-100 text-blue-700",
      interview: "bg-purple-100 text-purple-700",
      hired: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700"
    };
    return <Badge className={`${styles[status] || "bg-gray-100 text-gray-600"} font-medium capitalize`}>{status}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      completed: "status-completed",
      in_stock: "status-in-stock",
      low_stock: "status-low-stock",
      out_of_stock: "status-low-stock",
      active: "status-confirmed"
    };
    return <Badge className={`${styles[status] || "bg-gray-100 text-gray-600"} font-medium`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2D5016] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9] flex">
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed lg:relative z-40 h-full transition-all duration-300 ${
        sidebarOpen ? "w-64" : "w-0 lg:w-20"
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-[#E2E8E0]">
            <img 
              src={LOGO_URL} 
              alt="Clean Kin" 
              className={`transition-all ${sidebarOpen ? "h-12" : "h-8"}`} 
            />
          </div>

          <nav className="flex-1 p-4 space-y-2">
            {[
              { id: "overview", icon: LayoutDashboard, label: "Overview" },
              { id: "bookings", icon: Calendar, label: "Bookings" },
              { id: "quotes", icon: FileText, label: "Quote Requests" },
              { id: "enquiries", icon: Inbox, label: "Enquiries" },
              { id: "job-sheets", icon: ClipboardCheck, label: "Job Sheets" },
              { id: "cleaners", icon: KeyRound, label: "Cleaner Accounts" },
              { id: "recruitment", icon: Users, label: "Recruitment" },
              { id: "suppliers", icon: Truck, label: "Suppliers" },
              { id: "stock", icon: Package, label: "Stock" },
              { id: "laundry", icon: Shirt, label: "Laundry" }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id 
                    ? "bg-[#2D5016] text-white" 
                    : "text-[#5C6B57] hover:bg-[#E8F5E9]"
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-[#E2E8E0]">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-[#5C6B57] hover:bg-[#E8F5E9] rounded-xl transition-all"
              data-testid="btn-logout"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              {sidebarOpen && <span className="font-medium">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-[#E2E8E0] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-[#F9FAF9] rounded-lg"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <h1 className="text-xl font-semibold text-[#1A2E12] capitalize">
                {activeTab === "overview" ? "Dashboard" : activeTab}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#5C6B57]">Welcome, {adminName}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-white border-[#E2E8E0]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#5C6B57]">Total Revenue</p>
                        <p className="text-3xl font-bold text-[#1A2E12]" data-testid="stat-revenue">
                          £{stats.revenue.toFixed(2)}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-[#2D5016]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E2E8E0]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#5C6B57]">Total Bookings</p>
                        <p className="text-3xl font-bold text-[#1A2E12]" data-testid="stat-bookings">
                          {stats.bookings.total}
                        </p>
                        <p className="text-xs text-[#7CB342]">{stats.bookings.pending} pending</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-[#2D5016]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E2E8E0]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#5C6B57]">Stock Items</p>
                        <p className="text-3xl font-bold text-[#1A2E12]" data-testid="stat-stock">
                          {stats.stock.total}
                        </p>
                        {stats.stock.low_stock > 0 && (
                          <p className="text-xs text-red-500">{stats.stock.low_stock} low stock</p>
                        )}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <Package className="w-6 h-6 text-[#2D5016]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E2E8E0]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#5C6B57]">Suppliers</p>
                        <p className="text-3xl font-bold text-[#1A2E12]" data-testid="stat-suppliers">
                          {stats.suppliers}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <Truck className="w-6 h-6 text-[#2D5016]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Second row of stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-white border-[#E2E8E0]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#5C6B57]">Job Applications</p>
                        <p className="text-3xl font-bold text-[#1A2E12]" data-testid="stat-applications">
                          {stats.recruitment?.total || 0}
                        </p>
                        {stats.recruitment?.pending > 0 && (
                          <p className="text-xs text-[#7CB342]">{stats.recruitment.pending} pending review</p>
                        )}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#2D5016]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Bookings */}
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader>
                  <CardTitle className="text-[#1A2E12]">Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.slice(0, 5).map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-medium">{booking.customer_name}</TableCell>
                          <TableCell>{booking.service_name}</TableCell>
                          <TableCell>{booking.booking_date} {booking.booking_time}</TableCell>
                          <TableCell>£{booking.total_price.toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader>
                  <CardTitle className="text-[#1A2E12]">All Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-medium">{booking.customer_name}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{booking.customer_email}</p>
                              <p className="text-[#5C6B57]">{booking.customer_phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>{booking.service_name}</TableCell>
                          <TableCell>{booking.bedrooms}B/{booking.bathrooms}Ba</TableCell>
                          <TableCell>{booking.booking_date} {booking.booking_time}</TableCell>
                          <TableCell>£{booking.total_price.toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(booking.payment_status)}</TableCell>
                          <TableCell>{getStatusBadge(booking.status)}</TableCell>
                          <TableCell>
                            <Select 
                              value={booking.status}
                              onValueChange={(v) => updateBookingStatus(booking.id, v)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Suppliers Tab */}
          {activeTab === "suppliers" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-[#1A2E12]">Suppliers</CardTitle>
                  <Dialog open={supplierModal} onOpenChange={setSupplierModal}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#2D5016] hover:bg-[#1A2E12]"
                        onClick={() => {
                          setEditingItem(null);
                          setSupplierForm({ name: "", contact_email: "", contact_phone: "", address: "", products: "", notes: "" });
                        }}
                        data-testid="btn-add-supplier"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Supplier
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingItem ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Name</Label>
                          <Input value={supplierForm.name} onChange={(e) => setSupplierForm(p => ({...p, name: e.target.value}))} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Email</Label>
                            <Input type="email" value={supplierForm.contact_email} onChange={(e) => setSupplierForm(p => ({...p, contact_email: e.target.value}))} />
                          </div>
                          <div>
                            <Label>Phone</Label>
                            <Input value={supplierForm.contact_phone} onChange={(e) => setSupplierForm(p => ({...p, contact_phone: e.target.value}))} />
                          </div>
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input value={supplierForm.address} onChange={(e) => setSupplierForm(p => ({...p, address: e.target.value}))} />
                        </div>
                        <div>
                          <Label>Products (comma separated)</Label>
                          <Input value={supplierForm.products} onChange={(e) => setSupplierForm(p => ({...p, products: e.target.value}))} placeholder="Cleaning supplies, Eco products" />
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea value={supplierForm.notes} onChange={(e) => setSupplierForm(p => ({...p, notes: e.target.value}))} />
                        </div>
                        <Button className="w-full bg-[#2D5016]" onClick={handleSupplierSubmit}>
                          {editingItem ? "Update" : "Add"} Supplier
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Products</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliers.map((supplier) => (
                        <TableRow key={supplier.id}>
                          <TableCell className="font-medium">{supplier.name}</TableCell>
                          <TableCell>
                            <p>{supplier.contact_email}</p>
                            <p className="text-sm text-[#5C6B57]">{supplier.contact_phone}</p>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {supplier.products.slice(0, 3).map((p, i) => (
                                <Badge key={`perk-${p}`} variant="secondary" className="text-xs">{p}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(supplier.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditingItem(supplier);
                                setSupplierForm({...supplier, products: supplier.products.join(", ")});
                                setSupplierModal(true);
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSupplier(supplier.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Stock Tab */}
          {activeTab === "stock" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-[#1A2E12]">Stock Management</CardTitle>
                  <Dialog open={stockModal} onOpenChange={setStockModal}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#2D5016] hover:bg-[#1A2E12]"
                        onClick={() => {
                          setEditingItem(null);
                          setStockForm({ name: "", category: "", quantity: 0, unit: "", reorder_level: 10, supplier_id: "", eco_friendly: true });
                        }}
                        data-testid="btn-add-stock"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingItem ? "Edit Stock Item" : "Add Stock Item"}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Item Name</Label>
                          <Input value={stockForm.name} onChange={(e) => setStockForm(p => ({...p, name: e.target.value}))} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Category</Label>
                            <Select value={stockForm.category} onValueChange={(v) => setStockForm(p => ({...p, category: v}))}>
                              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cleaning">Cleaning Supplies</SelectItem>
                                <SelectItem value="equipment">Equipment</SelectItem>
                                <SelectItem value="consumables">Consumables</SelectItem>
                                <SelectItem value="laundry">Laundry Items</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Unit</Label>
                            <Input value={stockForm.unit} onChange={(e) => setStockForm(p => ({...p, unit: e.target.value}))} placeholder="bottles, boxes, etc" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Quantity</Label>
                            <Input type="number" value={stockForm.quantity} onChange={(e) => setStockForm(p => ({...p, quantity: parseInt(e.target.value) || 0}))} />
                          </div>
                          <div>
                            <Label>Reorder Level</Label>
                            <Input type="number" value={stockForm.reorder_level} onChange={(e) => setStockForm(p => ({...p, reorder_level: parseInt(e.target.value) || 0}))} />
                          </div>
                        </div>
                        <div>
                          <Label>Supplier</Label>
                          <Select value={stockForm.supplier_id} onValueChange={(v) => setStockForm(p => ({...p, supplier_id: v}))}>
                            <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                            <SelectContent>
                              {suppliers.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch checked={stockForm.eco_friendly} onCheckedChange={(v) => setStockForm(p => ({...p, eco_friendly: v}))} />
                          <Label>Eco-Friendly Product</Label>
                        </div>
                        <Button className="w-full bg-[#2D5016]" onClick={handleStockSubmit}>
                          {editingItem ? "Update" : "Add"} Item
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {stockItems.filter(i => i.status === "low_stock" || i.status === "out_of_stock").length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <span className="text-red-700">
                        {stockItems.filter(i => i.status === "low_stock" || i.status === "out_of_stock").length} items need restocking
                      </span>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Reorder Level</TableHead>
                        <TableHead>Eco</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stockItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.quantity} {item.unit}</TableCell>
                          <TableCell>{item.reorder_level}</TableCell>
                          <TableCell>
                            {item.eco_friendly ? (
                              <CheckCircle2 className="w-5 h-5 text-[#7CB342]" />
                            ) : (
                              <span className="text-[#5C6B57]">-</span>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditingItem(item);
                                setStockForm(item);
                                setStockModal(true);
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteStockItem(item.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Laundry Tab */}
          {activeTab === "laundry" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-[#1A2E12]">Laundry Services</CardTitle>
                  <Dialog open={laundryModal} onOpenChange={setLaundryModal}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#2D5016] hover:bg-[#1A2E12]"
                        onClick={() => {
                          setEditingItem(null);
                          setLaundryForm({ name: "", description: "", price_per_kg: 0, turnaround_days: 1, items_accepted: "" });
                        }}
                        data-testid="btn-add-laundry"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Service
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingItem ? "Edit Laundry Service" : "Add Laundry Service"}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Service Name</Label>
                          <Input value={laundryForm.name} onChange={(e) => setLaundryForm(p => ({...p, name: e.target.value}))} />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea value={laundryForm.description} onChange={(e) => setLaundryForm(p => ({...p, description: e.target.value}))} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Price per kg (£)</Label>
                            <Input type="number" step="0.01" value={laundryForm.price_per_kg} onChange={(e) => setLaundryForm(p => ({...p, price_per_kg: parseFloat(e.target.value) || 0}))} />
                          </div>
                          <div>
                            <Label>Turnaround (days)</Label>
                            <Input type="number" value={laundryForm.turnaround_days} onChange={(e) => setLaundryForm(p => ({...p, turnaround_days: parseInt(e.target.value) || 1}))} />
                          </div>
                        </div>
                        <div>
                          <Label>Items Accepted (comma separated)</Label>
                          <Input value={laundryForm.items_accepted} onChange={(e) => setLaundryForm(p => ({...p, items_accepted: e.target.value}))} placeholder="Bedding, Towels, Linens" />
                        </div>
                        <Button className="w-full bg-[#2D5016]" onClick={handleLaundrySubmit}>
                          {editingItem ? "Update" : "Add"} Service
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Price/kg</TableHead>
                        <TableHead>Turnaround</TableHead>
                        <TableHead>Items Accepted</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {laundryServices.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium">{service.name}</TableCell>
                          <TableCell className="max-w-xs truncate">{service.description}</TableCell>
                          <TableCell>£{service.price_per_kg.toFixed(2)}</TableCell>
                          <TableCell>{service.turnaround_days} day{service.turnaround_days > 1 ? 's' : ''}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {service.items_accepted.slice(0, 3).map((item, i) => (
                                <Badge key={`item-${item}`} variant="secondary" className="text-xs">{item}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(service.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditingItem(service);
                                setLaundryForm({...service, items_accepted: service.items_accepted.join(", ")});
                                setLaundryModal(true);
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteLaundryService(service.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Quote Requests Tab */}
          {activeTab === "quotes" && (
            <QuoteRequestsPanel
              quoteRequests={quoteRequests}
              onChanged={fetchAllData}
            />
          )}

          {/* Enquiries Tab */}
          {activeTab === "enquiries" && (
            <EnquiriesPanel
              enquiries={enquiries}
              onChanged={fetchAllData}
            />
          )}

          {/* Job Sheets Tab */}
          {activeTab === "job-sheets" && <JobSheetsPanel />}

          {/* Cleaner Accounts Tab */}
          {activeTab === "cleaners" && <CleanersPanel />}

          {/* Recruitment Tab */}
          {activeTab === "recruitment" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-[#E2E8E0]">
                <CardHeader>
                  <CardTitle className="text-[#1A2E12] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#2D5016]" />
                    Job Applications ({recruitmentApplications.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recruitmentApplications.length === 0 ? (
                    <div className="text-center py-12 text-[#5C6B57]">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No job applications yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Experience</TableHead>
                          <TableHead>DBS</TableHead>
                          <TableHead>Applied</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recruitmentApplications.map((app) => (
                          <TableRow key={app.id} data-testid={`application-row-${app.id}`}>
                            <TableCell className="font-medium">
                              {app.firstName} {app.surname}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm space-y-1">
                                <div className="flex items-center gap-1 text-[#5C6B57]">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-[150px]">{app.email}</span>
                                </div>
                                <div className="flex items-center gap-1 text-[#5C6B57]">
                                  <Phone className="w-3 h-3" />
                                  <span>{app.mobileTelephone}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-[#5C6B57]">
                                <MapPin className="w-3 h-3" />
                                <span>{app.postCode}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {app.experienceYears || 0}y {app.experienceMonths || 0}m
                            </TableCell>
                            <TableCell>
                              <Badge className={app.hasDBSCertificate === "yes" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                                {app.hasDBSCertificate === "yes" ? "Yes" : "No"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-[#5C6B57]">
                              {new Date(app.created_at).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell>{getApplicationStatusBadge(app.status)}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setApplicationDetailModal(true);
                                  }}
                                  data-testid={`view-application-${app.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Select 
                                  value={app.status}
                                  onValueChange={(v) => updateApplicationStatus(app.id, v)}
                                >
                                  <SelectTrigger className="w-28 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="reviewed">Reviewed</SelectItem>
                                    <SelectItem value="interview">Interview</SelectItem>
                                    <SelectItem value="hired">Hired</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Application Detail Modal */}
              <Dialog open={applicationDetailModal} onOpenChange={setApplicationDetailModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-[#1A2E12]">
                      Application Details
                    </DialogTitle>
                  </DialogHeader>
                  {selectedApplication && (
                    <div className="space-y-6 mt-4">
                      {/* Personal Info */}
                      <div className="bg-[#F9FAF9] rounded-xl p-4">
                        <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" /> Personal Information
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#5C6B57]">Name:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.firstName} {selectedApplication.surname}</p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Email:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.email}</p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Mobile:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.mobileTelephone}</p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Home Phone:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.homeTelephone || 'N/A'}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-[#5C6B57]">Address:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.homeAddress}, {selectedApplication.postCode}</p>
                          </div>
                        </div>
                      </div>

                      {/* Employment Info */}
                      <div className="bg-[#F9FAF9] rounded-xl p-4">
                        <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                          <Clock className="w-4 h-4" /> Employment Information
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#5C6B57]">Experience:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.experienceYears || 0} years, {selectedApplication.experienceMonths || 0} months</p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Has DBS Certificate:</span>
                            <p className="font-medium text-[#1A2E12]">{selectedApplication.hasDBSCertificate === "yes" ? "Yes" : "No"}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-[#5C6B57]">Previous Companies:</span>
                            <p className="font-medium text-[#1A2E12] whitespace-pre-wrap">{selectedApplication.previousCompanies || 'Not provided'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Availability */}
                      <div className="bg-[#F9FAF9] rounded-xl p-4">
                        <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Availability
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          {selectedApplication.availability && Object.entries(selectedApplication.availability).map(([day, data]) => (
                            data.available && (
                              <div key={day} className="bg-white rounded-lg p-2 border border-[#E2E8E0]">
                                <span className="capitalize font-medium text-[#1A2E12]">{day}</span>
                                <p className="text-[#5C6B57] text-xs">{data.from} - {data.to}</p>
                              </div>
                            )
                          ))}
                          {(!selectedApplication.availability || Object.values(selectedApplication.availability).every(d => !d.available)) && (
                            <p className="text-[#5C6B57] col-span-3">No availability specified</p>
                          )}
                        </div>
                      </div>

                      {/* Additional Info */}
                      {selectedApplication.additionalInfo && (
                        <div className="bg-[#F9FAF9] rounded-xl p-4">
                          <h3 className="font-semibold text-[#2D5016] mb-3">Additional Information</h3>
                          <p className="text-sm text-[#1A2E12] whitespace-pre-wrap">{selectedApplication.additionalInfo}</p>
                        </div>
                      )}

                      {/* Declarations */}
                      <div className="bg-[#E8F5E9] rounded-xl p-4">
                        <h3 className="font-semibold text-[#2D5016] mb-3">Declarations Confirmed</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#2D5016]" />
                            <span>Legally entitled to work in UK</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#2D5016]" />
                            <span>Has valid ID documents</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#2D5016]" />
                            <span>English language knowledge</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#2D5016]" />
                            <span>Confirmed info correct</span>
                          </div>
                        </div>
                      </div>

                      {/* Status Update */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#E2E8E0]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#5C6B57]">Current Status:</span>
                          {getApplicationStatusBadge(selectedApplication.status)}
                        </div>
                        <Select 
                          value={selectedApplication.status}
                          onValueChange={(v) => {
                            updateApplicationStatus(selectedApplication.id, v);
                            setSelectedApplication({...selectedApplication, status: v});
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Update Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="interview">Interview</SelectItem>
                            <SelectItem value="hired">Hired</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
